import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { request } from './OperationsProvider';
import DownloadTable from '../shell/DownloadTable';

// The platform's GPS hardware, and which customers may use each unit.
//
// Registering a device here is what creates it on the tracking server. That is the whole point of
// the screen: a unit that exists in this product but not on the tracking server is refused by the
// server on every report it sends, which from inside the product looks exactly like a tracker
// nobody switched on. One real device sat silent for a week that way.
//
// Granting is permission to use, not permission to watch. A customer may fit a granted unit to one
// of its trucks; whether it can see where that truck is depends on whether the truck is running
// that customer's trip or standing at that customer's site. The same unit can be granted to several
// customers at once, which is how a transporter's lorry serves more than one plant.
const blank = { identifier: '', name: '', model: '', simNumber: '', providerRef: '' };

const PlatformDevices = ({ tenants }) => {
  const [providers, setProviders] = useState([]);
  const [devices, setDevices] = useState([]);
  const [form, setForm] = useState(null);
  const [granting, setGranting] = useState(null);
  const [tenantId, setTenantId] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = async () => {
    const [p, d] = await Promise.all([
      request('/api/platform/tracking-providers'),
      request('/api/platform/devices'),
    ]);
    setProviders(p); setDevices(d);
  };
  useEffect(() => { load().catch((e) => setError(e.message)); }, []);

  const tenantName = useMemo(() => {
    const byId = new Map((tenants || []).map((t) => [String(t.id), t.name]));
    return (id) => byId.get(String(id)) || id;
  }, [tenants]);

  const submit = async (event) => {
    event.preventDefault();
    setPending(true); setError(null);
    try {
      const created = await request('/api/platform/devices', 'POST', {
        identifier: form.identifier.trim(),
        name: form.name.trim(),
        model: form.model.trim(),
        simNumber: form.simNumber.trim(),
        providerRef: Number(form.providerRef),
      });
      setForm(null);
      setNotice(`${created.identifier} registered on the tracking server as device ${created.providerDeviceId}.`);
      await load();
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  const grant = async () => {
    setPending(true); setError(null);
    try {
      await request(`/api/platform/devices/${granting.id}/grants`, 'POST', { tenantId });
      setGranting(null); setTenantId('');
      await load();
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  const revoke = async (device, to) => {
    setPending(true); setError(null);
    try {
      await request(`/api/platform/devices/${device.id}/grants/${to}`, 'DELETE');
      await load();
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  return (
    <Box sx={{ mt: 5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Typography variant="h6">GPS devices</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!providers.length}
          onClick={() => { setError(null); setForm({ ...blank, providerRef: String(providers[0]?.id || '') }); }}
        >
          Register device
        </Button>
      </Stack>
      <Typography color="text.secondary" sx={{ my: 2 }}>
        Registering a device creates it on the tracking server. Grant it to a customer so they can fit it to one of their vehicles.
      </Typography>

      {error && !form && !granting && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ my: 1 }}>{notice}</Alert>}
      {!providers.length && (
        <Alert severity="info" sx={{ my: 1 }}>
          No tracking server is configured for this deployment, so a device cannot be registered yet.
        </Alert>
      )}

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <DownloadTable
          name="platform-devices"
          rows={devices}
          columns={[
            ['Identifier', (d) => d.identifier],
            ['Name', (d) => d.name || ''],
            ['On tracking server', (d) => `#${d.providerDeviceId}`],
            // One row per device, so the workspaces it serves are one cell. A device may serve
            // several at once, which is the whole point of the platform registry.
            ['Granted to', (d) => (d.tenants || []).map((g) => g.grantedTo).join(' / ')],
            ['Workspaces', (d) => (d.tenants || []).length],
          ]}
        />
      </Stack>
      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Identifier', 'Name', 'On tracking server', 'Granted to', ''].map((c, i) => (
                <TableCell key={c || `actions${i}`} align={c === '' ? 'right' : 'left'}>{c}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!devices.length && (
              <TableRow><TableCell colSpan={5}><Typography color="text.secondary">No devices are registered yet.</Typography></TableCell></TableRow>
            )}
            {devices.map((d) => (
              <TableRow key={d.id}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{d.identifier}</TableCell>
                <TableCell>{d.name || '—'}</TableCell>
                <TableCell>{`#${d.providerDeviceId}`}</TableCell>
                <TableCell>
                  <Stack direction="row" gap={0.5} flexWrap="wrap">
                    {!(d.tenants || []).length && <Typography variant="caption" color="text.secondary">Nobody yet</Typography>}
                    {(d.tenants || []).map((g) => (
                      <Chip
                        key={g.grantedTo}
                        size="small"
                        label={tenantName(g.grantedTo)}
                        onDelete={() => revoke(d, g.grantedTo)}
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Grant to a customer">
                    <IconButton size="small" onClick={() => { setError(null); setGranting(d); setTenantId(''); }}><AddIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!form} onClose={() => setForm(null)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={submit}>
          <DialogTitle>Register a GPS device</DialogTitle>
          <DialogContent>
            <Stack gap={2} sx={{ mt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                required
                autoFocus
                label="Device identifier"
                helperText="The IMEI or unique id the device sends. It must match exactly, or the tracking server will reject its reports."
                value={form?.identifier || ''}
                onChange={(e) => setForm((f) => ({ ...f, identifier: e.target.value }))}
                inputProps={{ maxLength: 64 }}
              />
              <TextField label="Name (optional)" value={form?.name || ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} inputProps={{ maxLength: 100 }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                <TextField fullWidth label="Model (optional)" value={form?.model || ''} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))} inputProps={{ maxLength: 100 }} />
                <TextField fullWidth label="SIM number (optional)" value={form?.simNumber || ''} onChange={(e) => setForm((f) => ({ ...f, simNumber: e.target.value }))} inputProps={{ maxLength: 100 }} />
              </Stack>
              <TextField
                select
                required
                label="Tracking server"
                value={form?.providerRef || ''}
                onChange={(e) => setForm((f) => ({ ...f, providerRef: e.target.value }))}
              >
                {providers.map((p) => <MenuItem key={p.id} value={String(p.id)}>{`${p.name}${p.local ? ' (bundled)' : ''}`}</MenuItem>)}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setForm(null)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={pending}>Register</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={!!granting} onClose={() => setGranting(null)} fullWidth maxWidth="xs">
        <DialogTitle>{`Grant ${granting?.identifier || ''}`}</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Typography variant="body2" color="text.secondary">
              They can fit this device to one of their vehicles. They see its location only while it runs their trip or stands at their site.
            </Typography>
            <TextField select required label="Customer" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
              {(tenants || []).map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGranting(null)}>Cancel</Button>
          <Button variant="contained" disabled={pending || !tenantId} onClick={grant}>Grant</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlatformDevices;
